import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Icon3D from '../components/Icon3D';
import { getAnalytics, getStudySessions, getUserProgression, getWrongAnswers } from '../api/endpoints';
import ProgressionChart from '../components/ProgressionChart';
import ProgressionTable from '../components/ProgressionTable';
import CategorySummaryTable from '../components/CategorySummaryTable';
import { useTenant } from '../contexts/TenantContext';
import api from '../api/axios';
import { Link } from 'react-router-dom';
import {
  BookOpen, Target, Flame, Clock, TrendingUp, TrendingDown,
  ChevronRight, ChevronDown, ChevronLeft, AlertTriangle, Play,
  Calendar as CalendarIcon, Award, Zap, XCircle, CheckCircle2, Lock
} from 'lucide-react';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [subCategoryStats, setSubCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [expandedCategories, setExpandedCategories] = useState({});
  const [progressionData, setProgressionData] = useState(null);
  const [progressionTimeRange, setProgressionTimeRange] = useState('all_time');
  const [progressionLoading, setProgressionLoading] = useState(false);
  const [wrongAnswersData, setWrongAnswersData] = useState(null);
  const [wrongAnswersLoading, setWrongAnswersLoading] = useState(false);
  const [tierBlocked, setTierBlocked] = useState(false);
  const { tenant } = useTenant();

  // Session stats from localStorage (set by QuestionsPage during practice)
  const [sessionStats, setSessionStats] = useState({ correct: 0, total: 0, streak: 0 });
  const [sessionCategoryStats, setSessionCategoryStats] = useState({});

  const tenantName = tenant?.name || 'MedMCQ';

  useEffect(() => {
    document.title = `Analytics | ${tenantName}`;
  }, [tenantName]);

  // Load session stats from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('sessionStats');
      if (saved) setSessionStats(JSON.parse(saved));
    } catch (e) { /* ignore */ }
    try {
      const saved = localStorage.getItem('sessionCategoryStats');
      if (saved) setSessionCategoryStats(JSON.parse(saved));
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadAnalytics();
    loadSessions();
    loadSubCategoryStats();
    loadWrongAnswers();
  }, []);

  useEffect(() => {
    const loadProgression = async () => {
      try {
        setProgressionLoading(true);
        const res = await getUserProgression({ time_range: progressionTimeRange });
        setProgressionData(res.data);
      } catch (e) {
        // silently fail — progression charts only appear when user has enough data
      } finally {
        setProgressionLoading(false);
      }
    };
    loadProgression();
  }, [progressionTimeRange]);

  const loadAnalytics = async () => {
    try {
      const response = await getAnalytics({ _t: Date.now() });
      setAnalytics(response.data);
    } catch (error) {
      if (error?.response?.status === 403) {
        setTierBlocked(true);
      } else {
        console.error('Error loading analytics:', error);
        setAnalytics({
          total_questions: 0,
          correct_rate: 0,
          average_time_per_question: 0,
          study_days: 0,
          current_streak: 0,
          highest_streak: 0,
          category_performance: {}
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSessions = async () => {
    try {
      const response = await getStudySessions();
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      setSessions([]);
    }
  };

  const loadSubCategoryStats = async () => {
    try {
      const response = await api.get('/analytics/subcategory');
      setSubCategoryStats(response.data);
    } catch (error) {
      console.error('Error loading sub-category stats:', error);
      setSubCategoryStats([]);
    }
  };

  const loadWrongAnswers = async () => {
    try {
      setWrongAnswersLoading(true);
      const response = await getWrongAnswers();
      setWrongAnswersData(response.data);
    } catch (error) {
      console.error('Error loading wrong answers:', error);
      setWrongAnswersData(null);
    } finally {
      setWrongAnswersLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getSubCategoriesByCategory = () => {
    const grouped = {};
    subCategoryStats.forEach(stat => {
      if (!grouped[stat.category]) {
        grouped[stat.category] = [];
      }
      grouped[stat.category].push(stat);
    });
    return grouped;
  };

  // Get weak areas: from top 10 most-practiced, show worst 3-4 by accuracy
  const getWeakAreas = () => {
    if (!analytics?.category_performance) return [];
    const top10 = Object.entries(analytics.category_performance)
      .sort((a, b) => b[1].total_answered - a[1].total_answered)
      .slice(0, 10);
    return top10
      .sort((a, b) => a[1].accuracy - b[1].accuracy)
      .slice(0, 4);
  };

  // Session accuracy helpers
  const sessionCorrectRate = sessionStats.total > 0
    ? Math.round((sessionStats.correct / sessionStats.total) * 100)
    : 0;

  const getSessionCategoryAccuracy = (category) => {
    const catKey = category.toLowerCase();
    const catStats = sessionCategoryStats[catKey] || sessionCategoryStats[category];
    if (!catStats || catStats.total === 0) return null;
    return Math.round((catStats.correct / catStats.total) * 100);
  };

  const getTrend = (sessionVal, cumulativeVal) => {
    if (sessionVal === null || sessionVal === undefined) return 'none';
    if (sessionVal > (cumulativeVal || 0) + 2) return 'improving';
    if (sessionVal < (cumulativeVal || 0) - 2) return 'declining';
    return 'same';
  };

  const hasSessionData = sessionStats.total > 0;

  // Topic colors mapping — all MCQ categories + legacy clinical keys
  const topicColors = {
    anatomy:           '#06b6d4',
    physiology:        '#84cc16',
    pharmacology:      '#14b8a6',
    pathology:         '#6366f1',
    microbiology:      '#ec4899',
    biochemistry:      '#f97316',
    neuroscience:      '#a855f7',
    biostatistics:     '#3b82f6',
    'clinical skills': '#22c55e',
    embryology:        '#f59e0b',
    ethics:            '#8b5cf6',
    genetics:          '#ef4444',
    haematology:       '#dc2626',
    histology:         '#eab308',
    immunology:        '#0891b2',
    psychology:        '#c026d3',
    'public health':   '#10b981',
    radiology:         '#2563eb',
    cardiology:        '#ef4444',
    neurology:         '#a855f7',
    respiratory:       '#22c55e',
    default:           '#06b6d4',
  };

  const getTopicColor = (topic) => {
    const key = topic.toLowerCase();
    return topicColors[key] || topicColors.default;
  };

  const renderCalendar = () => {
    const monthNames = ["January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"];

    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const today = new Date();
    const todayLocalStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const studyDates = new Set(sessions.map(s => s.date));
    // If user has practiced today (sessionStats.total > 0), ensure today shows as a study day
    if (sessionStats.total > 0) {
      studyDates.add(todayLocalStr);
    }
    const dayHeaders = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const days = [];

    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isToday = today.getDate() === day &&
                      today.getMonth() === currentMonth &&
                      today.getFullYear() === currentYear;
      const hasActivity = studyDates.has(dateStr);

      days.push(
        <div
          key={day}
          className={`h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
            isToday
              ? 'ring-2 ring-blue-500 bg-blue-100 text-blue-900'
              : hasActivity
                ? 'bg-emerald-500 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {day}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Icon3D icon={CalendarIcon} colour="turquoise" size="sm" />
            <h2 className="text-lg font-bold text-slate-900">Study Calendar</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (currentMonth === 0) {
                  setCurrentMonth(11);
                  setCurrentYear(currentYear - 1);
                } else {
                  setCurrentMonth(currentMonth - 1);
                }
              }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <span className="font-semibold text-slate-900 min-w-[140px] text-center">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              onClick={() => {
                if (currentMonth === 11) {
                  setCurrentMonth(0);
                  setCurrentYear(currentYear + 1);
                } else {
                  setCurrentMonth(currentMonth + 1);
                }
              }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayHeaders.map(day => (
              <div key={day} className="text-center text-xs font-semibold text-slate-500 uppercase">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {days}
          </div>

          <div className="mt-4 flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-emerald-500 rounded"></div>
              <span className="text-slate-600">Study Day</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-blue-100 ring-2 ring-blue-500 rounded"></div>
              <span className="text-slate-600">Today</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (tierBlocked) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="bg-white/5 border border-white/10 rounded-xl p-8 max-w-md text-center">
            <Lock className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Full Analytics</h2>
            <p className="text-gray-400 mb-6">
              Upgrade to Explorer or above to unlock detailed analytics, session history, category breakdowns, and more.
            </p>
            <Link
              to="/subscription"
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-3 rounded-lg transition-colors"
            >
              View Plans
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-500">Loading analytics...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const weakAreas = getWeakAreas();
  const categoryPerformance = analytics?.category_performance || {};

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Overview Stats — Session vs All Time */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-6">
          {/* Total Questions */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={BookOpen} colour="blue" size="md" />
            </div>
            <div className="flex justify-between items-end mb-1">
              <div>
                <div className="text-xs font-semibold text-cyan-600 uppercase mb-0.5">This Session</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-900">{sessionStats.total}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-slate-400 uppercase mb-0.5">All Time</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-500">{analytics?.total_questions || 0}</div>
              </div>
            </div>
            <div className="text-sm text-slate-500">Questions Answered</div>
          </div>

          {/* Accuracy */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={Target} colour="green" size="md" />
              {hasSessionData && (
                <div className={`flex items-center gap-1 text-sm font-semibold ${
                  getTrend(sessionCorrectRate, analytics?.correct_rate) === 'improving' ? 'text-emerald-600' :
                  getTrend(sessionCorrectRate, analytics?.correct_rate) === 'declining' ? 'text-red-500' :
                  'text-slate-400'
                }`}>
                  {getTrend(sessionCorrectRate, analytics?.correct_rate) === 'improving' && <><TrendingUp className="w-4 h-4" /> ↑</>}
                  {getTrend(sessionCorrectRate, analytics?.correct_rate) === 'declining' && <><TrendingDown className="w-4 h-4" /> ↓</>}
                  {getTrend(sessionCorrectRate, analytics?.correct_rate) === 'same' && '→'}
                </div>
              )}
            </div>
            <div className="flex justify-between items-end mb-1">
              <div>
                <div className="text-xs font-semibold text-cyan-600 uppercase mb-0.5">This Session</div>
                <div className={`text-xl sm:text-2xl font-bold ${
                  hasSessionData
                    ? getTrend(sessionCorrectRate, analytics?.correct_rate) === 'improving' ? 'text-emerald-600' :
                      getTrend(sessionCorrectRate, analytics?.correct_rate) === 'declining' ? 'text-red-500' :
                      'text-slate-900'
                    : 'text-slate-900'
                }`}>{hasSessionData ? `${sessionCorrectRate}%` : '—'}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-slate-400 uppercase mb-0.5">All Time</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-500">{analytics?.correct_rate?.toFixed(1) || 0}%</div>
              </div>
            </div>
            <div className="text-sm text-slate-500">Accuracy</div>
          </div>

          {/* Day Streak */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={Flame} colour="turquoise" size="md" />
            </div>
            <div className="flex justify-between items-end mb-1">
              <div>
                <div className="text-xs font-semibold text-cyan-600 uppercase mb-0.5">Day Streak</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-900">{analytics?.day_streak || 0}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-slate-400 uppercase mb-0.5">Study Days</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-500">{analytics?.study_days || 0}</div>
              </div>
            </div>
            <div className="text-sm text-slate-500">Consecutive Days Studied</div>
          </div>

          {/* Study Time */}
          <div className="bg-white rounded-2xl border border-slate-200 p-3 sm:p-5" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="flex justify-between items-start mb-2 sm:mb-3">
              <Icon3D icon={Clock} colour="turquoise" size="md" />
            </div>
            <div className="flex justify-between items-end mb-1">
              <div>
                <div className="text-xs font-semibold text-cyan-600 uppercase mb-0.5">Correct Streak</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-900">{sessionStats.streak}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-semibold text-slate-400 uppercase mb-0.5">Best Ever</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-500">{analytics?.highest_streak || 0}</div>
              </div>
            </div>
            <div className="text-sm text-slate-500">Avg {analytics?.average_time_per_question?.toFixed(1) || 0}s/question</div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-3 gap-6 mb-6">
          {/* Topic Performance - 2/3 */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-bold text-slate-900">Topic Performance</h2>
                <span className="text-sm text-slate-500">
                  {Object.keys(categoryPerformance).length} topics
                </span>
              </div>
              {/* Legend */}
              <div className="flex gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-cyan-500 rounded-sm"></div>
                  <span className="text-slate-500">This Session</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 bg-slate-300 rounded-sm"></div>
                  <span className="text-slate-500">All Time</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-emerald-600 font-bold">↑</span>
                  <span className="text-slate-500">Improving</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-red-500 font-bold">↓</span>
                  <span className="text-slate-500">Needs Work</span>
                </div>
              </div>
            </div>
            <div className="p-6">
              {Object.keys(categoryPerformance).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(categoryPerformance)
                    .sort((a, b) => b[1].accuracy - a[1].accuracy)
                    .map(([category, data]) => {
                      const subCategories = getSubCategoriesByCategory()[category] || [];
                      const isExpanded = expandedCategories[category];
                      const color = getTopicColor(category);
                      const sessionAccuracy = getSessionCategoryAccuracy(category);
                      const trend = getTrend(sessionAccuracy, data.accuracy);

                      return (
                        <div key={category} className="group">
                          <div
                            className="cursor-pointer"
                            onClick={() => subCategories.length > 0 && toggleCategory(category)}
                          >
                            <div className="flex justify-between items-center mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-slate-700 capitalize">{category}</span>
                                {subCategories.length > 0 && (
                                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                )}
                                {sessionAccuracy !== null && (
                                  <span className={`text-xs font-bold ${
                                    trend === 'improving' ? 'text-emerald-600' :
                                    trend === 'declining' ? 'text-red-500' :
                                    'text-slate-400'
                                  }`}>
                                    {trend === 'improving' ? '↑' : trend === 'declining' ? '↓' : '→'}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3">
                                {sessionAccuracy !== null && (
                                  <span className="text-xs font-semibold text-cyan-600">
                                    Session: {sessionAccuracy}%
                                  </span>
                                )}
                                <span className="text-xs text-slate-500">
                                  {data.correct}/{data.total_answered}
                                </span>
                                <span className="text-sm font-bold" style={{ color }}>
                                  {data.accuracy?.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            {/* Session bar (cyan) — only if session data exists */}
                            {sessionAccuracy !== null && (
                              <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-1">
                                <div
                                  className="h-full rounded-full transition-all bg-cyan-500 relative overflow-hidden"
                                  style={{ width: `${sessionAccuracy || 0}%` }}
                                >
                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                                </div>
                              </div>
                            )}
                            {/* All-time bar (topic color) */}
                            <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all relative overflow-hidden"
                                style={{ width: `${data.accuracy || 0}%`, backgroundColor: color }}
                              >
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                              </div>
                            </div>
                          </div>

                          {/* Sub-categories */}
                          {isExpanded && subCategories.length > 0 && (
                            <div className="mt-3 ml-4 space-y-2 border-l-2 border-slate-200 pl-4">
                              {subCategories.map((subCat, idx) => (
                                <div key={idx}>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-slate-600">{subCat.sub_category}</span>
                                    <span className="text-xs font-medium text-slate-700">{subCat.accuracy}%</span>
                                  </div>
                                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full rounded-full relative overflow-hidden"
                                      style={{
                                        width: `${subCat.accuracy || 0}%`,
                                        backgroundColor: color
                                      }}
                                    >
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No topic data yet. Start practicing to see your performance!</p>
                </div>
              )}
            </div>
          </div>

          {/* Weak Areas - 1/3 */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" style={{ boxShadow: 'var(--shadow-card)' }}>
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <Icon3D icon={AlertTriangle} colour="amber" size="sm" />
                <h2 className="text-lg font-bold text-slate-900">Areas to Improve</h2>
              </div>
            </div>
            <div className="p-4">
              {weakAreas.length > 0 ? (
                <div className="space-y-3">
                  {weakAreas.map(([category, data]) => (
                    <div key={category} className="p-4 bg-slate-50 rounded-xl">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-semibold text-slate-900 capitalize">{category}</h4>
                          <p className="text-xs text-slate-500">
                            {data.correct}/{data.total_answered} correct
                          </p>
                        </div>
                        <span className="text-lg font-bold text-red-500">{data.accuracy?.toFixed(0)}%</span>
                      </div>
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden mb-3">
                        <div
                          className="h-full bg-red-500 rounded-full relative overflow-hidden"
                          style={{ width: `${data.accuracy || 0}%` }}
                        >
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/questions?category=${encodeURIComponent(category)}`)}
                        className="w-full py-2 text-white text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-2 relative overflow-hidden"
                        style={{
                          background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)',
                          boxShadow: 'var(--shadow-3d-btn)'
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full" style={{ animation: 'shimmer 3s infinite' }} />
                        <Play className="w-4 h-4 relative z-10" />
                        <span className="relative z-10">Practice Now</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Award className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                  <p className="font-medium text-slate-700">Great job!</p>
                  <p className="text-sm">No weak areas detected. Keep it up!</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Learning Progression Charts */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Your Learning Progression</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setProgressionTimeRange('all_time')}
                  className="px-3 py-1.5 rounded-lg font-medium text-sm transition-all text-white"
                  style={progressionTimeRange === 'all_time'
                    ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                    : { background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                  }
                >
                  All Time
                </button>
                <button
                  onClick={() => setProgressionTimeRange('last_30_days')}
                  className="px-3 py-1.5 rounded-lg font-medium text-sm transition-all text-white"
                  style={progressionTimeRange === 'last_30_days'
                    ? { background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                    : { background: 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)', boxShadow: 'var(--shadow-3d-btn)' }
                  }
                >
                  Last 30 Days
                </button>
              </div>
            </div>
          </div>
          <div className="p-5">
            {progressionLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (progressionData?.has_enough_data || progressionData?.overall_progression?.length > 0) ? (
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">Overall Accuracy Trend</h3>
                  <ProgressionChart data={progressionData.overall_progression} height={300} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-600 mb-3">Detailed Progression</h3>
                  <ProgressionTable data={progressionData.overall_progression} />
                </div>
                {progressionData.category_summary && progressionData.category_summary.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-600 mb-3">Category Summary</h3>
                    <CategorySummaryTable data={progressionData.category_summary} />
                  </div>
                )}
                {progressionData.category_progression && Object.keys(progressionData.category_progression).length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-600 mb-3">Progression by Category</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(progressionData.category_progression).map(([category, data]) => (
                        <div key={category} className="bg-slate-50 rounded-xl p-4">
                          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 capitalize">{category}</h4>
                          <ProgressionChart data={data} height={180} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <p className="text-sm">Complete 50+ questions to unlock your learning progression charts</p>
                <p className="text-xs mt-1">Charts will show your accuracy trend over time</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent Wrong Answers */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden mb-6" style={{ boxShadow: 'var(--shadow-card)' }}>
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-bold text-slate-900">Recent Wrong Answers</h2>
            </div>
            <div className="flex items-center gap-3">
              {wrongAnswersData?.total_wrong > 0 && (
                <a
                  href="/questions?review_wrong=true"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-lg shadow-sm hover:shadow-md transition-all"
                >
                  🎯 Practice These Again
                </a>
              )}
              {wrongAnswersData && (
                <span className="text-sm text-slate-500">
                  {wrongAnswersData.total_wrong} total — showing last 25
                </span>
              )}
            </div>
          </div>
          <div className="p-6">
            {wrongAnswersLoading ? (
              <div className="flex items-center justify-center h-24">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : wrongAnswersData?.wrong_answers?.length > 0 ? (
              <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Question</th>
                      <th className="px-4 py-3 text-left">Category</th>
                      <th className="px-4 py-3 text-left">Level</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Time</th>
                      <th className="px-4 py-3 text-left">Re-asked?</th>
                      <th className="px-4 py-3 text-left">Result</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {wrongAnswersData.wrong_answers.map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-slate-800 leading-snug text-xs">{item.question_preview}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-600 capitalize whitespace-nowrap text-xs">{item.category}</td>
                        <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">Level {item.difficulty}</td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                          {item.answered_at ? new Date(item.answered_at).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap text-xs">
                          {item.time_taken_seconds}s
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.re_asked ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-medium">
                              ↩ Yes ({item.re_attempt_count}×)
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs">Not yet</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {item.re_attempt_result === 'correct' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                              <CheckCircle2 className="w-3 h-3" /> Got it
                            </span>
                          )}
                          {item.re_attempt_result === 'wrong' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold">
                              <XCircle className="w-3 h-3" /> Still wrong
                            </span>
                          )}
                          {!item.re_attempt_result && (
                            <span className="text-slate-400 text-xs">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-400">
                <Award className="w-12 h-12 mx-auto mb-4 text-emerald-500" />
                <p className="font-medium text-slate-700">No wrong answers yet!</p>
                <p className="text-sm">Start practicing and any incorrect answers will appear here with re-attempt tracking.</p>
              </div>
            )}
          </div>
        </div>

        {/* Streak and Calendar Row */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Streak Stats */}
          <div className="rounded-2xl p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, rgb(6, 182, 212) 0%, rgb(8, 145, 178) 100%)' }}>
            <div className="flex items-center gap-4 mb-6 relative z-10">
              <Icon3D icon={Flame} colour="turquoise" size="sm" />
              <div>
                <p className="text-cyan-100 text-sm">Day Streak</p>
                <p className="text-3xl font-bold">{analytics?.day_streak || 0} {(analytics?.day_streak || 0) === 1 ? 'Day' : 'Days'}</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 relative z-10">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold">{analytics?.current_streak || 0}</p>
                <p className="text-cyan-100 text-sm">Correct Streak</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold">{analytics?.highest_streak || 0}</p>
                <p className="text-cyan-100 text-sm">Best Streak</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold">{analytics?.study_days || 0}</p>
                <p className="text-cyan-100 text-sm">Total Days</p>
              </div>
            </div>
          </div>

          {/* Calendar - 2/3 */}
          <div className="lg:col-span-2">
            {renderCalendar()}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
